// ============================================================
// contexts/ColLayoutContext.js
// 차트 열 레이아웃 (1/2/3열) 전역 Context
// 패턴: Context (싱글톤 Context 객체 + 편의 훅)
// 사용법: import { useColLayout } from '../../contexts/ColLayoutContext.js';
//
// 목적: colLayout state를 탭 컴포넌트 안의 grid div에 key prop으로
//       전달하여 열 변경 시 ResponsiveContainer를 강제 remount시킴.
//       CSS Custom Property 변경만으로는 Recharts ResizeObserver가
//       안정적으로 발동하지 않는 문제를 근본적으로 해결.
//
// 사용법:
//   [공급] ev-sim_v2_0.jsx:
//     <ColLayoutContext.Provider value={colLayout}>
//       ...탭 컴포넌트들...
//     </ColLayoutContext.Provider>
//
//   [소비] 각 탭 컴포넌트:
//     import { useColLayout } from '../../contexts/ColLayoutContext.js';
//     const colLayout = useColLayout();
//     <div key={colLayout} style={{gridTemplateColumns:'var(--chart-cols,1fr 1fr)', ...}}>
// ============================================================

import { createContext, useContext } from 'react';

/**
 * 차트 그리드 열 수(1/2/3)를 앱 전역에서 공유하는 React Context 객체.
 *
 * 기본값 2는 Provider 없이 호출될 때의 폴백(2열 레이아웃)이다.
 * 실제 앱에서는 ev-sim_v2_0.jsx의 Provider가 colLayout 상태를 공급한다.
 *
 * @type {React.Context<1|2|3>}
 */
export const ColLayoutContext = createContext(2);

/**
 * 현재 차트 열 수를 반환하는 편의 훅.
 *
 * 반환값을 그리드 컨테이너의 key prop에 전달하면 열 수 변경 시
 * Recharts ResponsiveContainer가 강제 remount되어 ResizeObserver가
 * 올바른 크기를 재측정한다.
 *
 * CSS Custom Property(--chart-cols)만 변경하는 방식은 ResizeObserver가
 * 안정적으로 발동하지 않아 이 패턴으로 대체하였다.
 *
 * @returns {1|2|3}  현재 차트 열 수
 */
export function useColLayout() {
  return useContext(ColLayoutContext);
}
